/**
 * Auth Guard Hook
 *
 * Provides authentication state and handles auth-related errors.
 * Redirects to login on 401 (Unauthorized) and to forbidden page on 403 (Forbidden).
 */

import { useEffect, useCallback } from "react";
import { useRouter } from "expo-router";
import { useAuth } from "@src/features/tenants/UserContext";
import { UnauthorizedError, ForbiddenError } from "../services/authenticated-fetch";
import type { UseAuthGuardOptions, UseAuthGuardReturn } from "../types";

export function useAuthGuard(options: UseAuthGuardOptions = {}): UseAuthGuardReturn {
  const { requireAuth = false, requiredRole } = options;
  const router = useRouter();
  const { isAuthenticated, isInitialized, user, accessToken, clearAuth } = useAuth();

  const isLoading = !isInitialized;

  // Check if user has a specific role from the access token
  const hasRole = useCallback(
    (role: string): boolean => {
      if (!accessToken) return false;

      try {
        // Decode the access token to check roles
        interface TokenPayload {
          "https://visioni.network/roles"?: string[];
          roles?: string[];
        }
        const payload = JSON.parse(atob(accessToken.split(".")[1])) as TokenPayload;
        const roles: string[] = payload["https://visioni.network/roles"] || payload.roles || [];
        return roles.includes(role);
      } catch {
        return false;
      }
    },
    [accessToken],
  );

  // Check if user has any of the specified roles
  const hasAnyRole = useCallback(
    (requiredRoles: string | string[]): boolean => {
      const rolesToCheck = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
      return rolesToCheck.some((role) => hasRole(role));
    },
    [hasRole],
  );

  // Handle API errors - redirect to login or forbidden page as appropriate
  const handleApiError = useCallback(
    (error: unknown) => {
      if (error instanceof UnauthorizedError) {
        // Clear auth state and redirect to home (AuthProvider will handle login)
        clearAuth();
        router.replace("/");
      } else if (error instanceof ForbiddenError) {
        // User is authenticated but lacks permissions
        router.replace("/forbidden" as never);
      }
      // Other errors are not handled here - let them propagate
    },
    [router, clearAuth],
  );

  // Redirect to home if auth is required but user is not authenticated
  // AuthProvider will handle showing login screen
  useEffect(() => {
    if (!isLoading && requireAuth && !isAuthenticated) {
      router.replace("/");
    }
  }, [isLoading, requireAuth, isAuthenticated, router]);

  // Redirect to forbidden if a specific role is required but user doesn't have any of them
  useEffect(() => {
    if (!isLoading && isAuthenticated && requiredRole && !hasAnyRole(requiredRole)) {
      router.replace("/forbidden" as never);
    }
  }, [isLoading, isAuthenticated, requiredRole, hasAnyRole, router]);

  return {
    isAuthenticated,
    isLoading,
    user,
    handleApiError,
    hasRole,
    hasAnyRole,
  };
}
